import {useContext, useMemo} from 'react'
import {AuthContext} from '../contexts/AuthContext'
import {getItemFromStorage} from '../utils/storage'
import useBusinessContext from './useBusinessContext'

/**
 * Returns the identity the current user is acting as when performing social
 * actions (posts, comments, likes, messages). When the business switcher has
 * a business selected, the user is acting as that business; otherwise they
 * are acting as themselves.
 *
 * Consumers should forward `businessId` to any mutation that accepts an
 * optional business author (e.g. likeFile, postComment, createPost).
 *
 * @returns {{
 *   businessId: string | null,
 *   business: object | null,
 *   actor: { id: string | null, name: string, avatarFilename: string | null },
 *   actorKind: 'user' | 'business',
 * }}
 */
const useActingAs = () => {
  const {selectedBusiness} = useBusinessContext()
  const {state: authState} = useContext(AuthContext) || {}

  return useMemo(() => {
    const user = authState?.user || getItemFromStorage({name: 'user'}) || {}

    if (selectedBusiness?.id) {
      return {
        businessId: selectedBusiness.id,
        business: selectedBusiness,
        actor: {
          id: selectedBusiness.id,
          name: selectedBusiness.name || '',
          avatarFilename: selectedBusiness.logoFilename || null,
        },
        actorKind: 'business',
      }
    }

    return {
      businessId: null,
      business: null,
      actor: {
        id: user?.id || null,
        name: user?.fullName || '',
        avatarFilename: user?.avatarFilename || null,
      },
      actorKind: 'user',
    }
  }, [selectedBusiness, authState?.user])
}

export default useActingAs
